import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { zodParseWithDetailedLog } from "src/api/apiErrorLogs";
import { z } from "zod";
import { SelectorProductSchema } from "./productSchema";

interface UseProductSelectorOptions {
    searchText?: string;
    enabled?: boolean;
    itemsPerPage?: number;
    intermediateProducts?: boolean;
    companyId?: number;
    campaignId?: number;
}

export const useProductSelector = (options: UseProductSelectorOptions = {}) => {
    const {
        searchText = "",
        enabled = true,
        itemsPerPage = 20,
        intermediateProducts = false,
        companyId,
        campaignId,
    } = options;

    const isQueryEnabled = enabled && campaignId !== undefined;

    const query = useQuery({
        queryKey: ["productSelector", searchText, itemsPerPage, intermediateProducts, companyId, campaignId],
        queryFn: async () => {
            const response = await loggedInApiCall.get("/api/products", {
                params: {
                    itemsPerPage,
                    "order[title]": "asc",
                    isIntermediate: intermediateProducts,
                    organization_id: companyId,
                    campaign: campaignId,
                    q: searchText !== "" ? searchText : undefined,
                    scores: {
                        subscores: false,
                    },
                },
            });

            return zodParseWithDetailedLog(response.data["hydra:member"], z.array(SelectorProductSchema), {
                operation: "GET",
                endpoint: "/products (selector)",
            });
        },
        enabled: isQueryEnabled,
        keepPreviousData: true,
        staleTime: 30_000,
    });

    return {
        products: query.data ?? [],
        isQueryEnabled,
        ...query,
        isLoading: query.isLoading,
        isFetching: query.isFetching,
    };
};
